/**
 * publicBookingPayload — PURE builder for the one-page "Confirm & book"
 * submission. Produces the single-JSON PAYLOAD CONTRACT (v1) of the coded BB
 * function `ratesearch-public-booking` — the authoritative shape lives in
 * primefreight-development/functions/ratesearch-public-booking/1.0/index.js
 * (top block comment); this builder is written against THAT comment, verbatim:
 *
 *   { captchaToken, email,
 *     customer:  { companyName, email, phone?, firstName?, lastName?,
 *                  street?, address2?, city?, state?, postal?, country? },
 *     shipper:   { name, email?, address1?, address2?, city?, state?,
 *                  postal?, country?, contactFirstName?, contactLastName? },
 *     broker:    { option: 'primeFreight'|'other', name? },   // REQUIRED
 *     booking:   { containerType, modeOfTransport, … carrierId,
 *                  originPortId, dischargePortId, … },        // camelCase
 *     sections:  { pickup?, delivery?, pickupCharges?, deliveryCharges?,
 *                  portOrigin?, portDischarge?, ers?, freightCharges? },
 *     insurance?: [row], dutyTax?, warehouse?, discount?, crm?, search? }
 *
 * All keys camelCase. Optional keys are OMITTED when empty (never null for
 * dates/enums). No redux, no IO — jest matrix suite in
 * publicBookingPayload.test.js.
 */
import moment from 'moment';
import {
  getBookingObject,
  updateBookingDataCharges,
} from './getBookingInfo';
import { buildSearchContext } from './searchContext';

/** @readonly @enum {string} Customs-broker choice (matches the BB contract). */
export const BrokerOption = Object.freeze({
  /** Prime Freight nominates the broker (recommended, +$75). */
  PRIME_FREIGHT: 'primeFreight',
  /** Customer brings their own broker ($0, carries `name`). */
  OTHER: 'other',
});

const ZERO_DUTY_TAX = Object.freeze({ duties: 0, taxes: 0 });

// ── small pure helpers ───────────────────────────────────────────────────────

/** Strip undefined/null/'' keys — the contract omits empty optionals. */
const omitEmpty = (obj) => {
  const out = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value === undefined || value === null || value === '') return;
    out[key] = value;
  });
  return out;
};

const numberOr = (value, fallback) => {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n;
};

/** First value that is neither null/undefined nor an empty string. */
const firstDefined = (...vals) => vals.find((v) => v != null && v !== '');

/** Normalize any of the app's date shapes to the contract's 'YYYY-MM-DD'. */
const toContractDate = (date) => {
  if (!date) return undefined;
  const strict = moment(date, [moment.ISO_8601, 'YYYY-MM-DD', 'DD MMM, YYYY'], true);
  if (strict.isValid()) return strict.format('YYYY-MM-DD');
  const loose = moment(new Date(date));
  return loose.isValid() ? loose.format('YYYY-MM-DD') : undefined;
};

/** Legacy charge-row shapes (card detail / adapters) → contract row shape. */
const toChargeRow = (row) => {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
  const price = numberOr(
    row.price != null
      ? row.price
      : row.displayed_total != null
        ? row.displayed_total
        : row.unit_cost,
    undefined
  );
  if (price === undefined) return null;
  return omitEmpty({
    chargeTypeId: row.chargetype_id != null ? row.chargetype_id : row.charge_type_id,
    description: row.charge_description || row.description || row.charge_code,
    price,
    quantity: numberOr(row.quantity, undefined),
    unitCode: row.unit_code || row.unitCode,
    currency: row.currency,
    comments: row.notes || row.comments,
    minimumPrice: numberOr(row.minimum_price, undefined),
    isEstimate: row.is_estimate === true ? true : undefined,
    accessorial: row.accessorial === true || row.is_accessorial === true ? true : undefined,
  });
};

const toChargeRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map(toChargeRow).filter(Boolean);

/**
 * Single-row fallback for a pickup/delivery leg: only when the mapped charge
 * list is empty and the selected option carries a scalar price.
 */
const toLegFallback = (sectionOption, mappedCharges) => {
  if (mappedCharges.length > 0) return undefined;
  const row = toChargeRow(sectionOption);
  return row || undefined;
};

const toBrokerBlock = (broker) => {
  const option =
    broker && broker.option === BrokerOption.OTHER
      ? BrokerOption.OTHER
      : BrokerOption.PRIME_FREIGHT;
  const name = broker && broker.name ? String(broker.name).trim() : '';
  return option === BrokerOption.OTHER && name ? { option, name } : { option };
};

/**
 * Insurance commodities → contract rows. Reads the LIVE CargoInsurance emit
 * shape (createCommodityObject: commodity_category_type / commodity_description
 * / commodity_value / quote.premium_amount / policy_id) as well as the plain
 * contract keys, so it works whether fed the real child state or a normalized row.
 */
const toInsuranceRow = (item) => {
  if (!item || typeof item !== 'object') return null;
  const quote = item.quote || {};
  const row = omitEmpty({
    category: firstDefined(item.category, item.commodity_category, item.commodity_category_type),
    description: firstDefined(item.description, item.commodityDescription, item.commodity_description),
    value: numberOr(firstDefined(item.commodityValue, item.value, item.commodity_value), undefined),
    premium: numberOr(
      quote.premium_amount != null ? quote.premium_amount : item.premium,
      undefined
    ),
    profitPercent: numberOr(item.profitPercent, undefined),
    policyReference: item.policy_id != null ? item.policy_id : item.policyReference,
    uuid: quote.insurance_uuid || item.uuid,
    quoteReference: item.quoteReference != null ? item.quoteReference : quote.reference,
    isReviewRequired: item.isReviewRequired === true ? true : undefined,
  });
  return Object.keys(row).length > 0 ? row : null;
};

// ── block builders ───────────────────────────────────────────────────────────

/** booking_data (getBookingObject) + card json → the contract `booking` block. */
const toBookingBlock = ({ bookingData, selectedCardJson, agentProfileId, insurance, cargoReadyDate }) => {
  const bd = bookingData || {};
  const isFcl = bd.container_type === 'FCL';

  const insuredValue = (insurance || []).reduce((sum, item) => {
    if (!item) return sum;
    // Accept the live CargoInsurance shape (commodity_value), the contract shape
    // (commodityValue) AND an already-normalized row (value) — consistent with toInsuranceRow.
    return sum + numberOr(firstDefined(item.commodityValue, item.commodity_value, item.value), 0);
  }, 0);
  const insuranceFee = (insurance || []).reduce(
    (sum, item) => sum + numberOr(item && item.quote && item.quote.premium_amount, 0),
    0
  );

  const fclOnly = isFcl
    ? {
        _20stCounter: numberOr(bd._20stCounter, 0),
        _40stCounter: numberOr(bd._40stCounter, 0),
        _40hqCounter: numberOr(bd._40hqCounter, 0),
        _45hqCounter: numberOr(bd._45hqCounter, 0),
        _20stPrice: numberOr(bd._20stPrice, undefined),
        _40stPrice: numberOr(bd._40stPrice, undefined),
        _40hqPrice: numberOr(bd._40hqPrice, undefined),
        _45hqPrice: numberOr(bd._45hqPrice, undefined),
      }
    : {};

  const lclAirOnly = !isFcl
    ? {
        freightRate: numberOr(bd.freight_rate, undefined),
        chargeableWeight: numberOr(bd.chargeable_weight, undefined),
      }
    : {};

  return omitEmpty({
    containerType: bd.container_type,
    modeOfTransport: bd.mode_of_transport,
    consigneeRole: bd.consignee_role,
    // cargoReadyDate = the shipper's ready-to-load date the user picked in the
    // search (queryParams.date), NOT the sailing's ETD. Falls back to etdOrigin.
    cargoReadyDate: cargoReadyDate || toContractDate(bd.ETD_origin),
    etdOrigin: toContractDate(bd.ETD_origin),
    // gateInDate intentionally omitted — the function defaults it to etdOrigin
    insuredValue: insuredValue > 0 ? insuredValue : undefined,
    insuranceFee: insuranceFee > 0 ? insuranceFee : undefined,
    isOverweight: !!(selectedCardJson && selectedCardJson.isOverweight),
    // poNumbers / styleNumbers: not collected on the public one-pager — omitted
    scheduleId: bd.schedule_id,
    carrierId: bd.carrier,
    vesselServiceId: bd.vessel_service,
    vendorContractId: bd.vendor_contract,
    // state.form.AgentInformation is an AgentProfile (getAllAgentProfiles), so
    // it travels as agentProfileId — the function resolves its agentEntity.
    agentProfileId,
    originPortId: bd.origin_port,
    dischargePortId: bd.discharge_port,
    destinationPortId: bd.destination_port,
    ...fclOnly,
    ...lclAirOnly,
  });
};

/** Adapter output (charge arrays incl. appended BROK/duty rows) → `sections`. */
const toSectionsBlock = (adapted, selectedCardJson) => {
  const pickupCharges = toChargeRows(adapted.pickup_section_charges);
  const deliveryCharges = toChargeRows(adapted.delivery_section_charges);
  const freightCharges = toChargeRows(
    selectedCardJson && selectedCardJson.freightCharges
  );

  return omitEmpty({
    pickup: toLegFallback(adapted.pickup_section, pickupCharges),
    delivery: toLegFallback(adapted.delivery_section, deliveryCharges),
    pickupCharges: pickupCharges.length ? pickupCharges : undefined,
    deliveryCharges: deliveryCharges.length ? deliveryCharges : undefined,
    portOrigin: toChargeRows(adapted.port_origin_section),
    portDischarge: toChargeRows(adapted.port_discharge_section),
    ers: toChargeRows(adapted.ers_section),
    freightCharges: freightCharges.length ? freightCharges : undefined,
  });
};

// ── the builder ──────────────────────────────────────────────────────────────

/**
 * Build the public-booking payload (contract v1).
 *
 * @param {Object} input
 * @param {string} input.captchaToken     invisible reCAPTCHA token (server-verified)
 * @param {Object} input.contact          { firstName, lastName, email, phone, role }
 * @param {Object} input.company          { companyName, street, address2, city, state, postal, country }
 * @param {Object} input.shipper          { companyName, email, phone?, firstName, lastName, street, address2, city, state, postal, country }
 * @param {Object} input.broker           { option: BrokerOption, name? }
 * @param {Object} input.finalSearchForm  state.route.final_search_form (selected card)
 * @param {Object} input.queryParams      state.route.queryParams
 * @param {Object} [input.agentInformation] state.form.AgentInformation (AgentProfile)
 * @param {string} [input.consigneeRole]  'Importer' | 'Exporter' (falls back to contact.role)
 * @param {Object} [input.discount]       state.route.discount ({ id, code, … })
 * @param {number} [input.leadId]         state.route.rateGate.leadId (CRM)
 * @param {Array}  [input.insurance]      booked-policy insured commodities (CargoInsurance emit shape)
 * @param {Object} [input.dutyTax]        { duties, taxes } subtotal (DutyAndTax)
 * @param {Object} [input.warehouse]      { requested, note } lightweight warehousing request
 * @param {Object} [input.fclCounters]    state.code_config.fclCounters (search context)
 * @param {Object} [input.lclShipment]    state.code_config.lclShipment
 * @param {Object} [input.airShipment]    state.code_config.airShipment
 * @returns {Object} the submission payload
 */
export const buildPublicBookingPayload = (input) => {
  const {
    captchaToken,
    contact,
    company,
    shipper,
    broker,
    finalSearchForm,
    queryParams,
    agentInformation,
    consigneeRole,
    discount,
    leadId,
    insurance,
    dutyTax,
    warehouse,
    fclCounters,
    lclShipment,
    airShipment,
  } = input || {};

  const role = consigneeRole || (contact && contact.role) || 'Importer';
  const brokerBlock = toBrokerBlock(broker);
  const insuranceArr = Array.isArray(insurance) ? insurance : [];
  const dutyTaxSubtotal = dutyTax || ZERO_DUTY_TAX;

  // Reuse the EXISTING booking adapters end-to-end. `discount` is injected so
  // the per-size price source matches the discounted card detail (same rule
  // getBookingObject applies for the logged-in flow); updateBookingDataCharges
  // appends the BROK (+ duty/tax) charge lines to portDischarge, as today.
  const adapted = getBookingObject(
    { ...(finalSearchForm || {}), discount: discount || undefined },
    queryParams || {},
    agentInformation,
    role
  );
  // The card-detail charge arrays are always present in the live flow; keep the
  // builder total anyway (updateBookingDataCharges spreads them).
  const toArray = (value) => (Array.isArray(value) ? value : []);
  adapted.port_discharge_section = toArray(adapted.port_discharge_section);
  adapted.port_origin_section = toArray(adapted.port_origin_section);
  adapted.ers_section = toArray(adapted.ers_section);
  adapted.pickup_section_charges = toArray(adapted.pickup_section_charges);
  adapted.delivery_section_charges = toArray(adapted.delivery_section_charges);

  const withBrokerAndDuty = updateBookingDataCharges(
    adapted,
    { custom_broker: brokerBlock.option, custom_broker_name: brokerBlock.name },
    dutyTaxSubtotal
  );

  const email = (contact && contact.email) || '';
  const c = contact || {};
  const co = company || {};
  const sh = shipper || {};
  const selectedCardJson = (finalSearchForm && finalSearchForm.selected_card_json) || {};

  const insuranceRows = insuranceArr.map(toInsuranceRow).filter(Boolean);
  const hasDutyTax =
    numberOr(dutyTaxSubtotal.duties, 0) > 0 || numberOr(dutyTaxSubtotal.taxes, 0) > 0;

  return omitEmpty({
    captchaToken: captchaToken || '',
    email,
    customer: omitEmpty({
      companyName: co.companyName,
      email,
      phone: c.phone,
      firstName: c.firstName,
      lastName: c.lastName,
      street: co.street,
      address2: co.address2,
      city: co.city,
      state: co.state,
      postal: co.postal,
      country: co.country,
    }),
    shipper: omitEmpty({
      name: sh.companyName || sh.name,
      email: sh.email,
      address1: sh.street || sh.address1,
      address2: sh.address2,
      city: sh.city,
      state: sh.state,
      postal: sh.postal,
      country: sh.country,
      contactFirstName: sh.firstName || sh.contactFirstName,
      contactLastName: sh.lastName || sh.contactLastName,
    }),
    broker: brokerBlock,
    booking: toBookingBlock({
      bookingData: withBrokerAndDuty.booking_data,
      selectedCardJson,
      agentProfileId: agentInformation && agentInformation.id,
      insurance: insuranceArr,
      // The shipper's ready-to-load date the user picked in the search.
      cargoReadyDate: toContractDate(queryParams && queryParams.date),
    }),
    sections: toSectionsBlock(withBrokerAndDuty, selectedCardJson),
    insurance: insuranceRows.length ? insuranceRows : undefined,
    dutyTax: hasDutyTax
      ? omitEmpty({
          duties: numberOr(dutyTaxSubtotal.duties, undefined),
          taxes: numberOr(dutyTaxSubtotal.taxes, undefined),
        })
      : undefined,
    discount:
      discount && (discount.id || discount.code)
        ? omitEmpty({ id: discount.id, code: discount.code })
        : undefined,
    // Lightweight warehousing request (no public RFQ backend) — rides into the
    // booking so pf-comms surfaces it in the sales@ notification for follow-up.
    warehouse:
      warehouse && warehouse.requested
        ? omitEmpty({ requested: true, note: warehouse.note })
        : undefined,
    crm: leadId != null ? { leadId } : undefined,
    search: buildSearchContext({
      queryParams,
      fclCounters,
      lclShipment,
      airShipment,
    }),
  });
};
